import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {Injectable} from "@angular/core";
import {Filer, tillatteFiltyper} from "felles/tjenester/Filer";
import {GooglePickerKonfigurasjonModell, SakSkyfilOpplastingRessursClient} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {GoogleLogin} from "./GoogleLogin";
import {i18n, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {BrukerkontekstTilbyder} from "felles-ng2/tjenester/BrukerkontekstTilbyder";
import {MeldingTilBruker} from "felles/tjenester/MeldingTilBruker";
import {ValgtFil} from "web-ng2/delt/komponenter/filvelger-skytjenester/komponenter/opplastingsstatus-skyfiler/opplastingsstatus-skyfiler";
import {ValgteSkyfiler} from "web-ng2/delt/komponenter/filvelger-skytjenester/SkyfilOpplasting";
import Picker = google.picker.Picker;
import ClientConfigError = google.accounts.oauth2.ClientConfigError;
import ResponseObject = google.picker.ResponseObject;

const debug = initDebugger('GoogleDriveFilvelgerGui');

/**
 * Tjeneste for å vise en filvelger som lar bruker velge et eller flere dokumenter fra sin google drive.
 * <pre>
 * Se https://developers.google.com/drive/picker
 *
 * Vi har et eget google cloud prosjekt der nødvendige api-tilganger er definert, se https://console.cloud.google.com/home/dashboard?project=lederplan-picker-api
 * Se lederplan/dokumentasjon/integrasjoner/google-drive.md for ytterligere info om integrasjonen.
 *
 * Google apiene gapi og google.picker blir tilgjengeliggjort via inkludering av
 * https://apis.google.com/js/api.js og https://accounts.google.com/gsi/client  i lederplan-klient/src/html/index-web.html.
 * Typescript definisjoner for disse apiene er inkludert i lederplan-klient/tsconfig.json.
 * </pre>
 */
@Injectable()
export class GoogleDriveFilvelgerGui {

    /** google docs filtyper vi tillater i tillegg til std tillatte filtyper definert i {@link Filer} */
    static googleDocsMimetyper = ['application/vnd.google-apps.document', 'application/vnd.google-apps.presentation', 'application/vnd.google-apps.spreadsheet', 'application/vnd.google-apps.drawing'];
    static googleDocsFilendinger = ['gdoc', 'gslides', 'gsheet', 'gdraw'];

    constructor(private avhengigheter: GoogleDriveFilvelgerGuiAvhengigheter, private kontekst: BrukerkontekstTilbyder, private googleLogin: GoogleLogin, private oversettelser: Oversettelser) {
        this.kontekst.brukerLoggetUtSomStrøm().subscribe(() => this.deinitialiaser());
    }

    private konfigurasjon = this.avhengigheter.googleDrivePickerkonfigurasjon(); //settes via hentOauthtoken()->initKonfigurasjon()
    private picker: Picker | null = null; //settes via initPicker()
    private googlePickerApiLaster = this.lastGooglePickerApi();

    /** Autentiserer bruker og viser google drive picker dialog.
     * Returnerer valgte filer, eller null/undefined dersom ingen filer ble valgt. */
    async visPickerGUI(): Promise<ValgteSkyfiler<GoogleDriveFil> | null> {
        debug('visPickerGUI')
        await this.googlePickerApiLaster;
        return this.visPicker();
    }

    //private hjelpemetoder

    private deinitialiaser() {
        this.picker?.dispose();
        this.picker = null;
        this.googleLogin.deinitialiser();
    }

    /** laster google picker apiet */
    private lastGooglePickerApi(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            gapi.load('picker', {
                callback: () => {
                    debug('lastGooglePickerApi:callback picker api lastet');
                    resolve();
                },
                onerror: (e: any) => {
                    debug('lastGooglePickerApi:onerror', e);
                    reject(`feil ved lasting av google picker api, error: ${e}`);
                }
            });
        });
    }

    private async visPicker(): Promise<ValgteSkyfiler<GoogleDriveFil> | null> {
        return new Promise<ValgteSkyfiler<GoogleDriveFil> | null>(async (resolve, reject) => {
            try {
                if (!this.picker) await this.initPicker();
                this.picker!.setVisible(true);
                this.picker!.setCallback(async (data: ResponseObject) => {
                    debug('visPicker:pickerCallback', data)
                    switch (data.action) {
                        case google.picker.Action.CANCEL:
                            return resolve(null); //bruker avbrøt
                        case google.picker.Action.PICKED:
                            return resolve(this.håndterDokumenterValgt(data));
                    }
                });

            } catch (e: any) {
                debug('visPicker exception', e)
                switch ((<ClientConfigError>e).type) {
                    case 'popup_closed':
                        return resolve(null); //bruker avbrøt autentiseringsdialogen
                    case 'popup_failed_to_open':
                        this.avhengigheter.feilmelding(this.oversettelser.oversett(i18n('Feil ved åpning av et popup-vindu. Du må tillate åpning av popup-vinduer i nettleseren din')));
                        return resolve(null); //popup ble blokkert
                }
                reject(e);
            }
        });
    }

    /** init av picker, se https://developers.google.com/drive/picker/reference.
     * Forutsetter av hentOauthtoken() har blitt kalt og resolvet slik at this.konfigurasjon og this.oauthToken er satt*/
    private async initPicker() {
        const konfigurasjon = await this.konfigurasjon;
        const token = await this.googleLogin.hentOauthToken(konfigurasjon.clientId);
        const mineDokumenterView = new google.picker.DocsView()
          .setIncludeFolders(true); //Show folders in the view items. Do not combine with setOwnedByMe.
        const sharedDriveView = new google.picker.DocsView()
          .setIncludeFolders(true) //Show folders in the view items. Do not combine with setOwnedByMe.
          .setEnableDrives(true); //Shows shared drives and the files they contain. Note: Feature.SUPPORT_DRIVES needs to be enabled when enabling this.

        const builder = new google.picker.PickerBuilder()
          .setTitle(this.oversettelser.oversett(i18n(('Velg dokument(er) du vil kopiere til Styreplan')))) //Set the dialog title.
          .setLocale(this.kontekst.autentisertBruker().bruker.spraak) //ISO 639 language code.
          .setMaxItems(10)//	Sets the maximum number of items a user can select.
          .setSelectableMimeTypes(this.tillatteMimetyper()) //Set the list of selectable MIME types. Use commas to separate MIME types if more than one is required.
          .enableFeature(google.picker.Feature.MULTISELECT_ENABLED) //Enable a picker feature.
          .enableFeature(google.picker.Feature.SUPPORT_DRIVES) //Whether shared drive items should be included in results. Before enabling this make sure to refer to Google Drive API documentation for enabling shared drives and make any necessary Drive Rest API changes.
          .addView(mineDokumenterView)//Add a View to the navigation pane, DocsView is a subclass of View that can be used for Google Drive views.
          .addView(sharedDriveView)//Add a View to the navigation pane, DocsView is a subclass of View that can be used for Google Drive views.
          .setAppId(konfigurasjon.appId) //Sets the Google Drive App ID needed to allow application to access the user's files via the Google Drive API.
          .setOAuthToken(token) //Sets an OAuth token to use for authenticating the current user. Depending on the scope of the token, only certain views display data. Valid scopes are Google Docs, Drive, Photos, YouTube.
          .setDeveloperKey(konfigurasjon.developerKey) //Sets the Browser API key obtained from Google API Console.
        this.picker = builder.build();
    }

    /**
     * Håndtere at bruker valgte dokument/avbrøt i google picker dialogen,
     * Se https://developers.google.com/drive/picker/reference/results for dokumentasjon av data
     *
     * eksempel på et google docs dokument:
     * {
     *  description: "Retningslinjer for bruk av kryptografiske kontroller for beskyttelse av\ninformasjon"
     *  driveSuccess: true
     *  embedUrl: "https://docs.google.com/document/d/1LE9KVrgR1-iasCFAsl4wi8cB8zmOoAN430lyJ08BQYE/preview"
     *  iconUrl: "https://drive-thirdparty.googleusercontent.com/16/type/application/vnd.google-apps.document"
     *  id: "1LE9KVrgR1-iasCFAsl4wi8cB8zmOoAN430lyJ08BQYE"
     *  isShared: true
     *  lastEditedUtc: 1650544033156
     *  mimeType: "application/vnd.google-apps.document"
     *  name: "A10.1.1 Policy for bruk av kryptografiske kontroller"
     *  organizationDisplayName: "Styreplan AS"
     *  serviceId: "doc"
     *  sizeBytes: 0
     *  type: "document"
     *  url: "https://docs.google.com/document/d/1LE9KVrgR1-iasCFAsl4wi8cB8zmOoAN430lyJ08BQYE/edit?usp=drive_web"
     * }
     */
    private async håndterDokumenterValgt(data: ResponseObject): Promise<ValgteSkyfiler<GoogleDriveFil> | null> {
        debug('håndterDokumenterValgt', data)
        this.picker!.setVisible(false); //skjul picker
        return {
            aksessToken: await this.googleLogin.hentOauthToken((await this.konfigurasjon).clientId),
            dokumenter: data.docs.map((doc: any) => ({
                filId: doc.id,
                filnavn: doc.name,
                mimeType: doc.mimeType,
                url: doc.url
            }))
        };
    }

    private tillatteMimetyper() {
        return [
            ...tillatteFiltyper.map(filtype => filtype.mimetype),
            ...GoogleDriveFilvelgerGui.googleDocsMimetyper
        ].join(',');
    }
}

@Injectable()
export class GoogleDriveFilvelgerGuiAvhengigheter {

    constructor(private sakSkyfilOpplastingRessurs: SakSkyfilOpplastingRessursClient, private meldingTilBruker: MeldingTilBruker) {
    }

    async googleDrivePickerkonfigurasjon(): Promise<GooglePickerKonfigurasjonModell> {
        return this.sakSkyfilOpplastingRessurs.googlePickerKonfigurasjon().somPromise();
    }

    feilmelding(melding: string) {
        this.meldingTilBruker.feilmelding(melding);
    }
}

export interface GoogleDriveFil extends ValgtFil {
    filId: string;
    mimeType: string;
    url: string;
}
